import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Stack,
} from "@mui/material";
import { useAuth } from "hooks/useAuth";
import { masterChartPath } from "constants/masterChartRoutes";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { db } from "services/firebase";
import { isEhrLinkConsentDue } from "services/cmr/ehrLinkConsentWindow";

import CareStartDashboardLogo from "components/dashboard/logos/CareStartDashboardLogo";
import CmrDashboardLogo from "components/dashboard/logos/CmrDashboardLogo";
import DischargeInstructionsDashboardLogo from "components/dashboard/logos/DischargeInstructionsDashboardLogo";
import MyMedicationsDashboardLogo from "components/dashboard/logos/MyMedicationsDashboardLogo";
import VoxPont2Logo from "features/voxpont2/components/VoxPont2Logo";

const CARESTART_TAGLINE = (
  <>
    Start the care process{" "}
    <Box component="span" sx={{ fontWeight: 700 }}>
      in your own language
    </Box>{" "}
    and create a well-structured brief for your clinician
  </>
);
const CARESTART_TAGLINE_FOOTNOTE =
  "70+ languages supported today. Most likely, yours.";

const ActionCard = ({ logo, title, description, footnote, path, onNavigate }) => (
  <Card sx={{ height: "100%" }}>
    <CardActionArea
      onClick={() => path && onNavigate(path)}
      sx={{ height: "100%", p: 2 }}
    >
      <CardContent sx={{ textAlign: "center" }}>
        {logo ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mx: "auto",
              mb: 1.5,
              maxWidth: "100%",
              minHeight: 72,
            }}
          >
            {logo}
          </Box>
        ) : null}
        {title ? (
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {title}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
        {footnote ? (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1, lineHeight: 1.4 }}
          >
            * {footnote}
          </Typography>
        ) : null}
      </CardContent>
    </CardActionArea>
  </Card>
);

const PatientHomePage = () => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const [dueLinks, setDueLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(Boolean(user?.uid));
  const [linksLoadError, setLinksLoadError] = useState(false);
  const [listenTick, setListenTick] = useState(0);

  // [BETA-VOXPONT2] Same gate as the patient drawer item (set `voxPontBeta: true`
  // on the user's Firestore doc to opt in).
  const isVoxPontBeta = Boolean(userData?.voxPontBeta);

  useEffect(() => {
    if (!user?.uid) {
      setDueLinks([]);
      setLinksLoading(false);
      setLinksLoadError(false);
      return undefined;
    }
    setLinksLoading(true);
    setLinksLoadError(false);
    const unsub = db
      .collection("ehrLinks")
      .doc(user.uid)
      .collection("links")
      .where("status", "==", "active")
      .onSnapshot(
        (snap) => {
          const rows = snap.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((link) => isEhrLinkConsentDue(link.nextConsentPromptAt));
          setDueLinks(rows);
          setLinksLoadError(false);
          setLinksLoading(false);
        },
        () => {
          setDueLinks([]);
          setLinksLoadError(true);
          setLinksLoading(false);
        },
      );
    return () => unsub();
  }, [user?.uid, listenTick]);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mt: { xs: 1, sm: 2 }, mb: 1 }}>
          Patient Home
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your home for health records, check-in, and medical tools
        </Typography>
      </Box>

      {linksLoadError ? (
        <DataLoadErrorAlert
          message="Unable to check linked provider consent."
          onRetry={() => setListenTick((n) => n + 1)}
          sx={{ mb: 2 }}
        />
      ) : null}

      {!linksLoading && !linksLoadError && dueLinks.length > 0 ? (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {dueLinks.map((link) => {
            const institution =
              link.institutionName || "Healthcare provider";
            return (
              <Alert
                key={link.id}
                severity="warning"
                data-testid="ehr-link-consent-banner"
              >
                Your connection with {institution} needs your OK to continue.{" "}
                <RouterLink to="/dashboard/linked-providers">
                  Review now
                </RouterLink>
              </Alert>
            );
          })}
        </Stack>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            logo={<CareStartDashboardLogo maxWidth={220} sx={{ width: "100%" }} />}
            description={CARESTART_TAGLINE}
            footnote={CARESTART_TAGLINE_FOOTNOTE}
            path="/carestart3"
            onNavigate={navigate}
          />
        </Grid>

        {isVoxPontBeta ? (
          <Grid item xs={12} sm={6} md={4}>
            <ActionCard
              logo={<VoxPont2Logo maxWidth={220} sx={{ width: "100%" }} />}
              description="Real-time, HIPAA-compliant speech translation for clinical conversations"
              path="/voxpont2"
              onNavigate={navigate}
            />
          </Grid>
        ) : null}

        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            logo={<CmrDashboardLogo maxWidth={220} sx={{ width: "100%" }} />}
            description="All available health records"
            path={masterChartPath()}
            onNavigate={navigate}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            logo={
              <DischargeInstructionsDashboardLogo
                maxWidth={220}
                sx={{ width: "100%" }}
              />
            }
            description="Instructions and summaries from your facility visits"
            path="/dashboard/discharge-instructions"
            onNavigate={navigate}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            logo={
              <MyMedicationsDashboardLogo maxWidth={220} sx={{ width: "100%" }} />
            }
            description="View and manage medications"
            path="/dashboard/medications"
            onNavigate={navigate}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            title="Linked Providers"
            description="Healthcare institutions connected to your MasterChart"
            path="/dashboard/linked-providers"
            onNavigate={navigate}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            title="Medical Library"
            description="Ask questions and explore medical topics"
            path="/dashboard/medical-library"
            onNavigate={navigate}
          />
        </Grid>

        {/* Phase 4: Medical SuperIntelligence link, PendingConversationsCard, Medication Tracking (re-enable post Twilio BAA) */}
      </Grid>
    </Box>
  );
};

export default PatientHomePage;
